import React, { useEffect, useMemo, useRef, useState } from 'react';

import { scrollTo } from '@/components/hooks/hash';
import useCheckInitialRender from '@/components/hooks/useCheckInitialRender';
import { ISubmitSearchPayload } from '@/components/hooks/useDealerSearch';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import { DealerHeader } from '@/components/sections/dealer/header';
import { DealerPanel } from '@/components/sections/dealer/panel';
import { IDealerPanelContent } from '@/components/sections/dealer/panel/content';
import { DealerResult } from '@/components/sections/dealer/result';
import { DealerSearchFilter } from '@/components/sections/dealer/search-filter';
import { DealerSearchFilterMobileDisplayToggle } from '@/components/sections/dealer/search-filter/mobile-display-toggle';
import { ButtonIconProps } from '@/components/shared/buttons/button-icon';
import { Section } from '@/components/shared/layout/section';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import {
  clearBodyLockScroll,
  enableBodyLockScroll,
} from '@/components/shared/utility/lock-scroll';
import { config } from '@/stitches.config';
import { IDealer } from '@/types/dealer/dealer';
import { IDistrict } from '@/types/dealer/district';
import { ILocation } from '@/types/dealer/location';
import { IVehicleType } from '@/types/services/contentstack';
import { IIcon } from '@/types/shared';

export enum DealerSeachFilterKey {
  VehicleType = 'vehicle-type',
  Showroom = 'showroom',
  Service = 'service-center',
  BodyPaint = 'body-paint',
  ChangeTire = 'change-tire',
  Overhaul = 'overhaul',
  SearchText = 'searchText',
}
export interface IDealerSearchFilter {
  [DealerSeachFilterKey.VehicleType]: IVehicleType;
  [DealerSeachFilterKey.SearchText]: string;
  [DealerSeachFilterKey.Showroom]: boolean;
  [DealerSeachFilterKey.Service]: boolean;
  [DealerSeachFilterKey.BodyPaint]: boolean;
  [DealerSeachFilterKey.ChangeTire]: boolean;
  [DealerSeachFilterKey.Overhaul]: boolean;
}
export enum MobileDealerSelectTab {
  List,
  Map,
}
export interface IMobileDealerSelectTab {
  key: MobileDealerSelectTab;
  icon: IIcon;
  label: string;
}
export interface IDealerStaticContent {
  title: string;
  description: string;
  message?: string;
  imageMobile?: ImageProps;
  imageDesktop?: ImageProps;
  searchCurrentLocationButton: ButtonIconProps;
  panel: IDealerPanelContent;
}
interface DealerProps {
  staticContent: IDealerStaticContent;
  vehicleType: IVehicleType;
  provinces: any[];

  preSelectedDealer?: IDealer;
  currentPosition: ILocation;
  dealers: IDealer[];
  districts: IDistrict[];
  submitSearch: (payload: ISubmitSearchPayload) => void;
  searchCurrentLocation: (vehicleType: IVehicleType) => void;
  isSearched: boolean;
  isLoadingDealer: boolean;
  selectedProvinceID: string;
  selectedDistrictID: string;
  onProvinceChange: (value: string) => void;
  onDistrictChange: (value: string) => void;
}
export type DealerWithMarkerState = IDealer & {
  isSelectFromMarker?: boolean;
};

export const DEFAULT_SEARCH_FILTER_DATA: IDealerSearchFilter = {
  [DealerSeachFilterKey.VehicleType]: 'lcv',
  [DealerSeachFilterKey.SearchText]: '',
  [DealerSeachFilterKey.Showroom]: false,
  [DealerSeachFilterKey.Service]: false,
  [DealerSeachFilterKey.BodyPaint]: false,
  [DealerSeachFilterKey.ChangeTire]: false,
  [DealerSeachFilterKey.Overhaul]: false,
};
export const Dealer = ({ provinces = [], ...props }: DealerProps) => {
  const isMaxLg = useMediaQuery(config.media.maxlg);
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(
    !!props.preSelectedDealer
  );
  const [currentDealer, setCurrentDealer] = useState<
    DealerWithMarkerState | undefined
  >(undefined);
  const [searchText, setSearchText] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<IDealerSearchFilter>({
    ...DEFAULT_SEARCH_FILTER_DATA,
    [DealerSeachFilterKey.VehicleType]: props.vehicleType,
  });
  const [hideFilter, setHideFilter] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);

  const showMapResult = useMemo(
    () => props.isLoadingDealer || props.isSearched,
    [props.isLoadingDealer, props.isSearched]
  );
  const showSearchFilter = useMemo(
    () => !(hideFilter && isMaxLg),
    [hideFilter, isMaxLg]
  );

  const onOpenDrawerFromMarker = (id: string) => {
    const foundedDealer = props.dealers.find((item) => item.uid === id);
    if (!foundedDealer) {
      setCurrentDealer(undefined);
      alert('ขออภัยไม่พบข้อมูลที่ท่านต้องการ');
      return;
    }

    setCurrentDealer({ ...foundedDealer, isSelectFromMarker: true });
    setIsOpenDrawer(true);
    isMaxLg ? enableBodyLockScroll() : clearBodyLockScroll();
  };
  const onOpenDrawer = (id: string) => {
    const foundedDealer = props.dealers.find((item) => item.uid === id);
    if (!foundedDealer) {
      setCurrentDealer(undefined);
      alert('ขออภัยไม่พบข้อมูลที่ท่านต้องการ');
      return;
    }

    setCurrentDealer(foundedDealer);
    setIsOpenDrawer(true);
    isMaxLg ? enableBodyLockScroll() : clearBodyLockScroll();
  };
  const onCloseDrawer = async () => {
    clearBodyLockScroll();
    setIsOpenDrawer(false);
    setCurrentDealer(undefined);
  };
  const handleSearchFilterChange = (name: any, value: any) => {
    const results = {
      ...searchFilter,
      [name]: value,
    };
    setSearchFilter(results);
  };
  const scrollToAfterSearchHandler = () => {
    setTimeout(() => {
      if (!props.isSearched) {
        filterRef.current && scrollTo(filterRef.current, 0, null, 'bottom');
        return;
      }

      if (isMaxLg) {
        toggleRef.current && scrollTo(toggleRef.current);
      } else {
        contentRef.current && scrollTo(contentRef.current);
      }
    }, 100);
  };
  const onSearchSubmit = () => {
    props.submitSearch({
      searchText,
      vehicleType: searchFilter[DealerSeachFilterKey.VehicleType],
      showroom: searchFilter[DealerSeachFilterKey.Showroom],
      service: searchFilter[DealerSeachFilterKey.Service],
      bodyPaint: searchFilter[DealerSeachFilterKey.BodyPaint],
      changeTire: searchFilter[DealerSeachFilterKey.ChangeTire],
      overhaul: searchFilter[DealerSeachFilterKey.Overhaul],
    });
    scrollToAfterSearchHandler();
  };
  const onClickCurrentPosition = () => {
    setSearchText('');
    setSearchFilter({
      ...searchFilter,
      [DealerSeachFilterKey.Service]: false,
      [DealerSeachFilterKey.Showroom]: false,
    });
    props.searchCurrentLocation(searchFilter[DealerSeachFilterKey.VehicleType]);
    scrollToAfterSearchHandler();
  };

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          onSearchSubmit();
          break;
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.selectedProvinceID,
    props.selectedDistrictID,
    searchText,
    searchFilter,
  ]);

  useEffect(() => {
    if (!props.isSearched || !contentRef.current) return;

    !hideFilter
      ? filterRef.current && scrollTo(filterRef.current)
      : toggleRef.current && scrollTo(toggleRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideFilter]);

  const isInitialRender = useCheckInitialRender();

  useEffect(() => {
    if (!isInitialRender) return;

    const searchParams = new URLSearchParams(window.location.search);

    const vehicleParams = searchParams.get('vehicle');
    const provinceParams = searchParams.get('province');
    const districtParams = searchParams.get('district');
    const serviceParams = searchParams.get('service');
    const showroomParams = searchParams.get('showroom');
    let vehicleType: IVehicleType =
      process.env.NEXT_PUBLIC_WEB === 'lcv' ? 'lcv' : 'cv';
    if (vehicleParams === 'lcv' || vehicleParams === 'cv') {
      vehicleType = vehicleParams;
    }

    const province = provinceParams ?? '';
    props.onProvinceChange(province as string);
    const district = districtParams ?? '';
    district && props.onDistrictChange(district as string);

    const service = serviceParams === '1';
    const showroom = showroomParams === '1';
    setSearchFilter({
      ...searchFilter,
      [DealerSeachFilterKey.VehicleType]: vehicleType,
      [DealerSeachFilterKey.Showroom]: showroom,
      [DealerSeachFilterKey.Service]: service,
    });

    if (!vehicleParams || !provinceParams) return;

    props.submitSearch({
      provinceID: `${province}`,
      districtIDs: `${district}`,
      vehicleType,
      showroom,
      service,
    });
    scrollToAfterSearchHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialRender]);

  return (
    <>
      <Section test="section_dealer">
        <DealerHeader
          title={props.staticContent.title}
          description={props.staticContent.description}
          searchCurrentPositionButton={{
            label: props.staticContent.searchCurrentLocationButton.label,
            icon: props.staticContent.searchCurrentLocationButton.icon,
            tracking: props.staticContent.searchCurrentLocationButton.tracking,
            test: props.staticContent.searchCurrentLocationButton.test,
            isLoading: props.isLoadingDealer,
            handleClick: onClickCurrentPosition,
          }}
        />
        {!!showSearchFilter && (
          <DealerSearchFilter
            ref={filterRef}
            searchFilter={searchFilter}
            provinceID={props.selectedProvinceID}
            districtID={props.selectedDistrictID}
            provinces={provinces}
            districts={props.districts}
            isLoadingDealer={props.isLoadingDealer}
            searchValue={searchText}
            onHandleChange={handleSearchFilterChange}
            onSubmit={onSearchSubmit}
            onProvinceChange={props.onProvinceChange}
            onDistrictChange={props.onDistrictChange}
            onSearchChange={setSearchText}
          />
        )}
        {!!props.isSearched && (
          <DealerSearchFilterMobileDisplayToggle
            hideFilter={hideFilter}
            ref={toggleRef}
            onClickToggle={() => setHideFilter(!hideFilter)}
          />
        )}

        <DealerResult
          ref={contentRef}
          beforeSearchText={props.staticContent.message ?? ''}
          beforeSearchImage={props.staticContent.imageDesktop}
          dealers={props.dealers}
          selectedDealer={currentDealer}
          isLoadingDealer={props.isLoadingDealer}
          showMapResult={showMapResult}
          vehicleType={searchFilter[DealerSeachFilterKey.VehicleType]}
          currentLocation={props.currentPosition}
          onSelectDealer={onOpenDrawer}
          onSelectDealerFromMarker={onOpenDrawerFromMarker}
        />
      </Section>
      <DealerPanel
        isOpen={isOpenDrawer}
        panel={props.staticContent.panel}
        dealer={currentDealer}
        onClose={onCloseDrawer}
      />
    </>
  );
};

Dealer.displayName = 'SectionDealer';
